/**
 * Syncs the PDP top unit price with the Hulk Discounts offer table.
 * Top price follows the tier that matches the current quantity (falls back to the first tier).
 */
(function () {
  const TABLE_ROOT_SELECTORS = [
    '.hulkapps-volumes',
    '.hulkapps-volume-discount-tiers',
    '[class*="hulkapps-volume"]',
  ];

  const TIER_PRICE_SELECTORS = [
    '.hulkapps-price',
    '.hulk-offer-text',
    '[class*="hulkapps-price"]',
  ];

  function getProductInfo(root) {
    return (root || document).querySelector('product-info') || document.querySelector('[id^="MainProduct"]');
  }

  function getPriceContainer(productInfo) {
    if (!productInfo) return null;
    const sectionId = productInfo.dataset.section;
    return (
      productInfo.querySelector(`#price-${sectionId}`) ||
      productInfo.querySelector('.price-container') ||
      productInfo.querySelector('[id^="price-"]')
    );
  }

  function getSellingPriceEl(priceContainer) {
    if (!priceContainer) return null;
    return (
      priceContainer.querySelector('.price--on-sale .price-item--sale') ||
      priceContainer.querySelector('.price__regular .price-item--regular') ||
      priceContainer.querySelector('.price-item--regular')
    );
  }

  function getHulkTable(productInfo) {
    if (!productInfo) return null;
    for (const selector of TABLE_ROOT_SELECTORS) {
      const table = productInfo.querySelector(selector);
      if (table) return table;
    }
    return document.querySelector(TABLE_ROOT_SELECTORS.join(', '));
  }

  function getQuantity(productInfo) {
    const input =
      productInfo?.querySelector('quantity-input .quantity__input') ||
      productInfo?.querySelector('input[name="quantity"]');
    const value = parseInt(input?.value, 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  function parseQuantityFromLabel(text) {
    if (!text) return null;
    const cleaned = text.replace(/,/g, '').trim();

    // "10 - 24", "10–24", "10 to 24"
    let match = cleaned.match(/(\d+)\s*[-–—to]+\s*(\d+)/i);
    if (match) {
      return { min: parseInt(match[1], 10), max: parseInt(match[2], 10) };
    }

    // "10+", "10 or more", "Buy 10+"
    match = cleaned.match(/(\d+)\s*\+/);
    if (match) {
      return { min: parseInt(match[1], 10), max: Infinity };
    }

    match = cleaned.match(/(\d+)/);
    if (match) {
      const min = parseInt(match[1], 10);
      return { min, max: Infinity };
    }

    return null;
  }

  function extractPriceText(node) {
    if (!node) return '';
    for (const selector of TIER_PRICE_SELECTORS) {
      const priceNode = node.querySelector?.(selector);
      if (priceNode?.textContent?.trim()) return priceNode.textContent.trim();
    }

    // Prefer the last cell in a row (Quantity | Price)
    if (node.matches?.('tr') || node.querySelector?.('td, th')) {
      const cells = node.querySelectorAll('td, th');
      if (cells.length > 1) {
        const last = cells[cells.length - 1].textContent.trim();
        if (last) return last;
      }
    }

    return (node.textContent || '').trim();
  }

  function getTierRows(table) {
    if (!table) return [];

    const bodyRows = Array.from(table.querySelectorAll('tbody tr')).filter((row) => {
      const text = row.textContent.trim();
      return text && !/quantity|price|buy more|save more/i.test(text.split('\n')[0] || '');
    });
    if (bodyRows.length) return bodyRows;

    // Some Hulk layouts render tiers as stacked blocks instead of table rows
    const blocks = Array.from(
      table.querySelectorAll(
        '.hulkapps-volume-discount-tiers > *, [class*="tier"], [class*="offer-item"]'
      )
    ).filter((el) => extractPriceText(el));
    return blocks;
  }

  function pickTierPrice(table, quantity) {
    const rows = getTierRows(table);
    if (!rows.length) {
      // Fallback: first price-looking node in the table
      for (const selector of TIER_PRICE_SELECTORS) {
        const el = table.querySelector(selector);
        if (el?.textContent?.trim()) return el.textContent.trim();
      }
      return '';
    }

    let matchedPrice = extractPriceText(rows[0]);
    let matchedMin = -1;

    rows.forEach((row) => {
      const labelCell = row.querySelector?.('td, th, [class*="qty"], [class*="quantity"]') || row;
      const range = parseQuantityFromLabel(labelCell.textContent || '');
      const priceText = extractPriceText(row);
      if (!priceText || !range) return;

      if (quantity >= range.min && quantity <= range.max && range.min >= matchedMin) {
        matchedMin = range.min;
        matchedPrice = priceText;
      }
    });

    return matchedPrice;
  }

  function normalizeMoneyText(text) {
    if (!text) return '';
    // Keep the first money-looking token; drop trailing labels like "/ea"
    const match = text.match(
      /(?:[€£$]|CA\$|US\$|A\$|NZ\$)?\s*\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?\s*(?:[€£$])/
    );
    return (match ? match[0] : text).replace(/\s+/g, ' ').trim();
  }

  function syncPrice(root) {
    const productInfo = getProductInfo(root);
    if (!productInfo) return false;

    const table = getHulkTable(productInfo);
    const priceContainer = getPriceContainer(productInfo);
    const sellingPriceEl = getSellingPriceEl(priceContainer);
    if (!table || !sellingPriceEl) return false;

    const quantity = getQuantity(productInfo);
    const tierPrice = normalizeMoneyText(pickTierPrice(table, quantity));
    if (!tierPrice) return false;

    if (sellingPriceEl.dataset.hulkSyncedPrice === tierPrice) return true;
    sellingPriceEl.textContent = tierPrice;
    sellingPriceEl.dataset.hulkSyncedPrice = tierPrice;
    return true;
  }

  function scheduleSync(root, attemptsLeft = 12) {
    const run = () => {
      const synced = syncPrice(root);
      if (!synced && attemptsLeft > 0) {
        window.setTimeout(() => scheduleSync(root, attemptsLeft - 1), 250);
      }
    };
    window.requestAnimationFrame(run);
  }

  function observeHulkTable(productInfo) {
    if (!productInfo || productInfo.dataset.hulkPriceSyncBound === 'true') return;
    productInfo.dataset.hulkPriceSyncBound = 'true';

    const observer = new MutationObserver(() => scheduleSync(productInfo));
    observer.observe(productInfo, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    productInfo.addEventListener('change', (event) => {
      if (event.target?.matches?.('input[name="quantity"], .quantity__input, input[name="id"]')) {
        scheduleSync(productInfo);
      }
    });

    productInfo.addEventListener('input', (event) => {
      if (event.target?.matches?.('input[name="quantity"], .quantity__input')) {
        scheduleSync(productInfo);
      }
    });
  }

  function init(root) {
    const productInfo = getProductInfo(root);
    if (!productInfo) return;
    observeHulkTable(productInfo);
    scheduleSync(productInfo);
  }

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => scheduleSync(document));
    subscribe(PUB_SUB_EVENTS.quantityUpdate, () => scheduleSync(document));
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => scheduleSync(document));
  } else {
    // constants.js / pubsub.js load deferred; retry binding shortly after
    window.setTimeout(() => {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, () => scheduleSync(document));
        subscribe(PUB_SUB_EVENTS.quantityUpdate, () => scheduleSync(document));
        subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => scheduleSync(document));
      }
    }, 500);
  }
})();
